<template>
  <LocationCell
    :country="countryText"
    :country-code="address.country?.isoCode ?? null"
    :detail="region"
    :title="countryTooltip ?? countryText"
    data-test="location"
  />

  <CopyableText
    v-if="address.formattedAddress"
    :text="address.formattedAddress"
    :icon="WdsIconNameEnum.LOCATION"
    :title="locationTooltip"
    data-test="location-text"
  />
  <WdsStack v-else align-center gap-sm>
    <WdsIcon :name="WdsIconNameEnum.LOCATION" :size="16" class="tw-flex-shrink-0" />

    <WdsText
      data-test="location-text"
      :text="locationText"
      :title="locationTooltip ?? locationText"
      primary
      class="tw-line-clamp-1"
    />
  </WdsStack>

  <WdsStack v-if="showCurrency" align-center gap-sm>
    <WdsIcon :name="marketIcon" :size="16" class="tw-flex-shrink-0" />

    <WdsText
      data-test="market-text"
      :text="currency"
      :title="currency"
      primary
      class="tw-line-clamp-1"
    />
  </WdsStack>
</template>

<script setup lang="ts">
import { WdsIconNameEnum, WdsStack, WdsIcon, WdsText } from '@worksome/wds-components'
import { computed, ComputedRef } from 'vue'
import { Address, Market } from '@/gql/graphql'
import CopyableText from '@/components/CopyableText.vue'
import LocationCell from '@/components/LocationCell.vue'

const props = withDefaults(
  defineProps<{
    showCurrency: boolean
    address: Address
    market?: Pick<Market, 'code'> | null
    currency: string
    /** Region line under the country; empty renders nothing. */
    region?: string | null
    /** Row explanations; a row without one has no tooltip. */
    locationTooltip?: string
    countryTooltip?: string
  }>(),
  {
    market: null,
    region: undefined,
    locationTooltip: undefined,
    countryTooltip: undefined,
  },
)

const marketIcon: ComputedRef<WdsIconNameEnum> = computed(() => {
  if (!props.market?.code) {
    return WdsIconNameEnum.EARTH
  }

  return WdsIconNameEnum[`FLAG_${props?.market?.code}`]
})

const locationText = computed(() => {
  if (!props.address.state && !props.address.city) {
    return '-'
  }

  if (props.address.state && props.address.city) {
    return `${props.address.city}, ${props.address.state}`
  }

  return (props.address.city !== '' ? props.address.city : null) ?? `-, ${props.address.state}`
})

const countryText = computed(() => props.address.country?.name ?? '-')
</script>
